import { PAYMENT_CATALOG, TRIAL_PLAN } from './paymentCatalog';

const CATALOG_LIMIT_TO_DB = Object.freeze({
  properties: 'limite_propriedades',
  fields: 'limite_talhoes',
  sensors: 'limite_sensores',
  alerts: 'limite_alertas',
  users: 'limite_usuarios',
  ndvi: 'limite_ndvi_mes',
});

const UI_LIMIT_TO_DB = Object.freeze({
  propriedades: 'limite_propriedades',
  talhoes: 'limite_talhoes',
  sensores: 'limite_sensores',
  alertas: 'limite_alertas',
  usuarios: 'limite_usuarios',
  ndviMes: 'limite_ndvi_mes',
  irrigacaoTalhoes: 'limite_irrigacao_talhoes',
  compartilhamentosEnviar: 'limite_compartilhamentos_enviar',
  compartilhamentosReceber: 'limite_compartilhamentos_receber',
  downloadPrevisao: 'download_previsao',
  relatoriosPdf: 'relatorios_pdf',
  relatoriosExcel: 'relatorios_excel',
  sensoresPeriodoCompleto: 'sensores_periodo_completo',
  gestaoEquipe: 'gestao_equipe',
});

function catalogEntry(planId) {
  if (planId === 'starter') return TRIAL_PLAN;
  return PAYMENT_CATALOG[planId] || null;
}

function catalogFeatureDefaults(planId) {
  const collaborationReady = process.env.REACT_APP_COLLABORATION_ENABLED === 'true';
  const professional = planId === 'professional' || planId === 'management' || planId === 'enterprise';
  const management = planId === 'management' || planId === 'enterprise';
  const sharingLimits = {
    sensor_free: 0,
    starter: collaborationReady ? 1 : 0,
    essential: collaborationReady ? 1 : 0,
    professional: 2,
    management: 9,
    enterprise: null,
  };
  return {
    download_previsao: !['starter', 'sensor_free'].includes(planId),
    relatorios_pdf: planId !== 'sensor_free',
    relatorios_excel: professional,
    sensores_periodo_completo: true,
    gestao_equipe: management,
    limite_compartilhamentos_enviar: sharingLimits[planId] ?? null,
    limite_compartilhamentos_receber: !collaborationReady && ['starter', 'sensor_free'].includes(planId) ? 0 : null,
    limite_irrigacao_talhoes: planId === 'sensor_free' ? 0 : (planId === 'starter' ? 3 : (planId === 'essential' ? 10 : null)),
  };
}

export function buildCatalogPlanConfig(planId) {
  const plan = catalogEntry(planId);
  if (!plan) return null;

  const config = {
    plano: planId,
    nome_exibicao: plan.name,
    badge_texto: plan.name,
    badge_cor: plan.color || '#0787b8',
    ...catalogFeatureDefaults(planId),
  };

  Object.entries(plan.limits || {}).forEach(([catalogKey, value]) => {
    const dbKey = CATALOG_LIMIT_TO_DB[catalogKey];
    if (dbKey) config[dbKey] = value;
  });

  return config;
}

/**
 * O catálogo versionado é a fonte comercial do frontend. A configuração do
 * banco continua fornecendo campos extras, mas não pode sobrescrever os
 * limites dos planos atuais com valores antigos.
 */
export function resolvePlanConfig(planId, databaseConfig) {
  const catalogConfig = buildCatalogPlanConfig(planId);
  if (!catalogConfig) return databaseConfig || null;
  return { ...(databaseConfig || {}), ...catalogConfig };
}

export function mapPlanLimits(planConfig) {
  if (!planConfig) return null;
  return Object.fromEntries(
    Object.entries(UI_LIMIT_TO_DB).map(([uiKey, dbKey]) => [uiKey, planConfig[dbKey] ?? null])
  );
}

export function mergeUsageStatsWithPlanLimits(stats, limits) {
  if (!stats || !limits) return stats;
  const resourceToLimit = {
    propriedades: limits.propriedades,
    talhoes: limits.talhoes,
    sensores: limits.sensores,
    alertas: limits.alertas,
  };

  return Object.fromEntries(Object.entries(stats).map(([resource, stat]) => {
    if (!stat || !(resource in resourceToLimit)) return [resource, stat];
    const limit = resourceToLimit[resource];
    const used = Number(stat.usado || 0);
    const unlimited = limit === null;
    const percent = unlimited || !Number.isFinite(Number(limit)) || Number(limit) <= 0
      ? 0
      : Math.min(100, Math.round((used / Number(limit)) * 100));
    return [resource, {
      ...stat,
      limite: limit,
      ilimitado: unlimited,
      percentual: percent,
      permitido: unlimited || used < Number(limit),
    }];
  }));
}
